import React, { useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAuth0 } from '@auth0/auth0-react';
import { IAdminTask, TaskSubjectKind, useAdminTaskAssigneesQuery, useAdminTasksQuery } from '../../api/admin';
import { formatDate, relativeDay } from '../../utils/dates';
import OverviewPanel from './OverviewPanel';
import StatTile from './StatTile';
import TaskDialog from './TaskDialog';
import TaskTrailList from './TaskTrailList';
import { taskTypeLabel } from './taskOptions';

/** Shown in place of the tasks when this admin has no manage_tasks. */
const NO_ACCESS = 'no access to tasks';

/** Whose tree to show: everything tagged to the user or account, or to anything above or below it. */
export type TaskTrailSubject = { userId: number } | { accountId: number };

/** The tree's tasks plus what the tile derives from them. `forbidden` = a 403, this admin has no manage_tasks. */
export function useTaskTrail(subject: TaskTrailSubject) {
	const { data, isLoading, error } = useAdminTasksQuery(subject);
	const forbidden = (error as any)?.response?.status === 403;
	const tasks = data ?? [];
	const last = tasks.reduce<IAdminTask | null>(
		(best, t) =>
			t.status === 'done' && t.completed_date !== null && (!best || t.completed_date > best.completed_date!) ? t : best,
		null,
	);
	const openCount = tasks.filter((t) => t.status !== 'done').length;
	return { tasks, isLoading, forbidden, last, openCount };
}

interface TileProps {
	subject: TaskTrailSubject;
}

/** When we last did something for them, and how much is still open. Sits beside "Last activity". */
export function LastTouchpointTile({ subject }: TileProps) {
	const { isLoading, forbidden, last, openCount } = useTaskTrail(subject);
	if (forbidden) {
		return <StatTile label="Last touchpoint" value={null} caption={NO_ACCESS} />;
	}
	const who = last ? last.assignee_name ?? last.assignee_email ?? 'unassigned' : '';
	return (
		<StatTile
			label="Last touchpoint"
			value={relativeDay(last?.completed_date ?? null)}
			caption={last ? formatDate(last.completed_date) : 'no task done yet'}
			secondary={
				last
					? `${taskTypeLabel(last.type)} · ${who}${openCount ? ` · ${openCount} open` : ''}`
					: openCount
					? `${openCount} open`
					: undefined
			}
			loading={isLoading}
		/>
	);
}

interface PanelProps {
	subject: TaskTrailSubject;
	/** How the subject reads in a new task's "Tag to" box; falls back to its id until it is known. */
	subjectName?: string;
	/** Match the height of the neighbouring column and scroll inside, instead of sizing to the rows. */
	fill?: boolean;
	/** When not filling: scroll inside past this height. */
	maxHeight?: number;
}

/**
 * The Tasks box under the info card on a user's or an account's page: open items on top, then the
 * trail of what was done. "+ New" opens the full form already tagged to that user or account.
 */
export default function TaskTrailPanel({ subject, subjectName, fill = false, maxHeight }: PanelProps) {
	const { tasks, isLoading, forbidden } = useTaskTrail(subject);
	const { user } = useAuth0();
	const { data: assignees = [] } = useAdminTaskAssigneesQuery();
	const [createOpen, setCreateOpen] = useState(false);
	const ownKind: TaskSubjectKind = 'userId' in subject ? 'user' : 'account';
	const subjectId = 'userId' in subject ? subject.userId : subject.accountId;
	const defaultSubject = useMemo(
		() => ({ kind: ownKind, id: subjectId, name: subjectName ?? `${ownKind === 'user' ? 'User' : 'Account'} #${subjectId}` }),
		[ownKind, subjectId, subjectName],
	);
	if (forbidden) {
		return (
			<OverviewPanel title="Tasks" sx={{ height: fill ? '100%' : 'auto' }}>
				<Typography variant="body2" color="text.secondary">
					You don't have access to tasks (needs the manage tasks permission).
				</Typography>
			</OverviewPanel>
		);
	}
	// New tasks start assigned to whoever is adding them, as on the Tasks page.
	const myId = assignees.find((u) => u.email.toLowerCase() === user?.email?.toLowerCase())?.id ?? null;
	return (
		<>
			<OverviewPanel
				title="Tasks"
				action={
					<Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
						New
					</Button>
				}
				sx={{ height: fill ? '100%' : 'auto' }}
			>
				<TaskTrailList tasks={tasks} loading={isLoading} ownKind={ownKind} fill={fill} maxHeight={maxHeight} />
			</OverviewPanel>
			<TaskDialog
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				defaultAssigneeId={myId}
				defaultSubject={defaultSubject}
			/>
		</>
	);
}
